import { DEFAULT_PAGER, TDataTablePager } from '@monetize/types/common';
import { useEffect, useMemo, useRef } from 'react';
import { useGetAccountContacts } from '../../../../../api/contactsService';
import { MainSearchInput } from '../../../../../components/Monetize/MCustomSelect/components/MainSearchInput';
import { QuoteBasicContactSelect } from '../../../../../components/Quotes/QuoteBasicContactSelect';

const accountContactsPager: TDataTablePager = {
  ...DEFAULT_PAGER,
  sortField: 'fullName',
  sortOrder: 1,
  rows: 100,
};

interface ManageQuoteOfferingGroupsContactCellProps {
  accountId: string;
  contactId: string;
  placeholder: string;
  isReadOnly?: boolean;
  onChange: (contactId: string) => void;
}

/**
 * Compact contact picker for a group row. Rows sharing an account share one
 * request — react-query dedupes by key, and the common case is every group
 * billing to the same account.
 *
 * Creating and editing contacts stays in the group drawer; this modal only
 * picks from what already exists.
 */
export const ManageQuoteOfferingGroupsContactCell = ({
  accountId,
  contactId,
  placeholder,
  isReadOnly = false,
  onChange,
}: ManageQuoteOfferingGroupsContactCellProps) => {
  const { data: contacts } = useGetAccountContacts(
    {
      accountId,
      config: accountContactsPager,
      isGetAll: true,
    },
    {
      enabled: !!accountId,
      select: (response) => response?.content || [],
      refetchOnWindowFocus: false,
    },
  );

  /**
   * The account we have already resolved a default contact for. Seeded with the
   * account this cell opened on, so nothing is auto-filled just by opening the
   * modal — only a deliberate account change picks a contact.
   */
  const defaultedAccountIdRef = useRef(accountId);

  // Changing the account clears the contact, so offer the account's first
  // contact rather than leaving the row invalid. Contacts arrive a beat after
  // the account changes, hence keying off the fetched list.
  useEffect(() => {
    if (
      isReadOnly ||
      defaultedAccountIdRef.current === accountId ||
      !contacts?.length
    ) {
      return;
    }
    defaultedAccountIdRef.current = accountId;
    if (!contactId) {
      onChange(contacts[0].id);
    }
  }, [accountId, contacts, contactId, isReadOnly, onChange]);

  const selectedContact = useMemo(
    () => (contacts || []).find(({ id }) => id === contactId) || null,
    [contacts, contactId],
  );

  return (
    <QuoteBasicContactSelect
      value={selectedContact}
      contacts={contacts || []}
      accountId={accountId || undefined}
      onChange={(contact) => onChange(contact?.id || '')}
      isDisabled={!accountId}
      isReadOnly={isReadOnly}
      // No `variant: 'error'` here: that variant pins `pl: 8px` to make room for
      // its 3px left border, which overrides the padding InputGroup adds for the
      // search icon and drops the placeholder underneath it. Required-field
      // messages render under the field instead.
      showContentInInput={false}
      placeholder={placeholder}
      popOverProps={{ positioning: { sameWidth: true } }}
      MainInputComponent={MainSearchInput}
      allowContactCreation={false}
    />
  );
};
